import { Code } from "@/backend/models/code";
import { PARENT_TYPES, ROOT_TYPES, SourceTypes } from "./containerTypes";
import { CodeCreateParams, CodeParams } from "./codeTypes";
import { UpdatedParamsAndRefs } from "./parameterTypes";
import { FusaReply } from "./replyTypes";
import { Block } from "@/backend/models/block";
import { Commanding, CommandingCreateParams, CommandingParams } from "@/backend/models/commanding";

export enum operationTypes {
  File = 'File',
  FileInput = 'FileInput',
  Text = 'Text',
  Warning = 'Warning',
  Danger = 'Danger',
  Information = 'Information',
  FieldInput = 'FieldInput',
  Field = 'Field',
  Code = 'Code',
  Table = 'Table',
  Grapher = 'Grapher',
  Notes = 'Notes',
  Commanding = 'Commanding',
  Equipment = 'Equipment',
}

export const OperationParameterTypes = [
  operationTypes.Field,
  operationTypes.FieldInput,
  operationTypes.File,
  operationTypes.FileInput,
  operationTypes.Table,
];

export const CalloutOperationTypes = [
  operationTypes.Warning,
  operationTypes.Danger,
  operationTypes.Information,
];

export interface OperationInterface {
  id: string;
  content: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  type: operationTypes;
  parentId: string;
  position: number;
  title: string;
  inputConstraint: string;
  isScaffold: boolean;
  code?: Code;
  graph?: GraphParams;
  rootId: string;
  rootType: ROOT_TYPES;
  parentType: PARENT_TYPES;
  rangeParameterId: string;
  skipParameterId: string;
  skipKeyId: string;
  sourceId: string;
  sourceType?: SourceTypes;
  commanding?: Commanding;
  equipmentId?: string;
  collectedFileName?: string;
};

export interface GraphParams {
  id: string;
  description: string;
  xAxisLabel: string;
  yAxisLabel: string;
  sampleRate?: number;
  operationId: string;
  updatedAt: string;
  createdAt: string;
  /** The org of the graph's operation. GraphAccess sets it from the writer org; callers never choose it. */
  propelOrgId?: string;
}

export type OperationCreateParams = {
  type: operationTypes,
  parentId: string,
  content?: string,
  position: number,
  title?: string,
  isScaffold: boolean,
  sourceId?: string,
  sourceType?: SourceTypes,
  inputConstraint?: string,
  rangeParameterId?: string,
  skipParameterId?: string,
  skipKeyId?: string,
  identifier?: string,
  code?: Omit<CodeCreateParams, 'operationId'>;
  graph?: Omit<GraphParams, 'id' | 'operationId' | 'createdAt' | 'updatedAt'>;
  rootId: string;
  rootType: ROOT_TYPES;
  parentType: PARENT_TYPES;
  commanding?: Omit<CommandingCreateParams, 'operationId'>;
  equipmentId?: string;
  collectedFileName?: string;
};

export interface OperationParams extends OperationCreateParams {
  id: string;
  equipmentId?: string;
  graph?: GraphParams;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  type: operationTypes;
  parentId: string;
  content?: string;
  title?: string;
  isScaffold: boolean;
  sourceId?: string;
  sourceType?: SourceTypes;
  inputConstraint?: string;
  code?: CodeParams;
  rangeParameterId?: string;
  skipParameterId?: string;
  skipKeyId?: string;
  rootId: string;
  rootType: ROOT_TYPES;
  parentType: PARENT_TYPES;
  commanding?: CommandingParams;
  collectedFileName?: string;
  /** The org of the operation's parent. OperationAccess sets it on create from the writer org; callers never choose it. */
  propelOrgId?: string;
}

export interface ReadOperationRequest {
  id: string;
}

export interface ReadOperationReply extends FusaReply {
  operation: OperationParams;
}

export interface UpdateOperationRequest {
  operation: OperationInterface;
  hideToast?: boolean;
}

export interface UpdateOperationReply extends UpdatedParamsAndRefs, FusaReply {
  updatedOperations: OperationInterface[];
}

export interface SaveBlockRequest {
  operation: OperationParams;
  blockTitle: string;
}

export interface SaveBlockReply extends FusaReply {
  block: Block;
  status: number;
}
